import type { ReactNode } from "react";
import { useLingui } from "@lingui/react/macro";
import { Timer, AlertCircle, Image as ImageIcon, Video, GripVertical, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { formatTimer, type RecipeStep } from "./types";

interface StepSidebarCardProps {
  step: RecipeStep;
  index: number;
  isSelected: boolean;
  showDragHandle?: boolean;
  onClick: () => void;
  /**
   * The viewer's clickable critical-control-point icon (`CcpStepFlag`), drawn
   * over the card's corner. It is a sibling of the card's button, never inside
   * it. Without it a flagged step shows a plain, unclickable marker, as in the
   * editor.
   */
  ccpFlag?: ReactNode;
}

export function StepSidebarCard({
  step,
  index,
  isSelected,
  showDragHandle = false,
  onClick,
  ccpFlag,
}: StepSidebarCardProps) {
  const { t } = useLingui();

  const hasTimer = !!step.timerEnabled && (step.timer ?? 0) > 0;
  const hasWarning = !!step.warningEnabled;
  const hasCcpMarker = step.isCcp === true && !ccpFlag;
  const hasPhoto = step.mediaUrls?.some((u) => !u.includes("video")) ?? false;
  const hasVideo = step.mediaUrls?.some((u) => u.includes("video")) ?? false;
  const hasMedia = hasPhoto || hasVideo;
  const tempLabel = step.temperature
    ? `${step.temperature}°${step.temperatureUnit ?? "C"}`
    : null;

  const card = (
    <Button
      variant="ghost"
      onClick={onClick}
      className={cn(
        "h-auto w-full items-start justify-start rounded-[10px] border p-3 text-left",
        // Room for the icon over the corner.
        ccpFlag && "pr-11",
        isSelected
          ? "border-(--color-border) bg-(--color-surface-muted) hover:bg-(--color-surface-muted)"
          : "border-(--color-border) bg-(--color-surface-content) hover:bg-(--color-surface-muted)",
      )}
    >
      <div className="flex min-w-0 flex-1 items-start gap-4">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-[6px] bg-(--color-action)">
          <span className="text-sm font-medium text-white">{index + 1}</span>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          {(hasTimer || hasWarning || hasCcpMarker) && (
            <div className="flex flex-wrap items-center gap-2">
              {hasTimer && (
                <span className="inline-flex items-center gap-1 rounded-full bg-(--brand-blue) px-2 py-1 text-sm font-medium text-(--brand-white)">
                  <Timer className="size-4" />
                  {formatTimer(step.timer!, step.timerUnit)}
                </span>
              )}
              {hasWarning && (
                <span className="inline-flex items-center gap-1 rounded-full bg-(--color-danger)/60 px-2 py-1 text-sm font-medium text-(--brand-white)">
                  <AlertCircle className="size-4" />
                  {t`Warning`}
                </span>
              )}
              {hasCcpMarker && (
                <span className="inline-flex items-center gap-1 rounded-full bg-(--color-danger-bg) px-2 py-1 text-sm font-medium text-(--color-danger-text)">
                  <TriangleAlert className="size-4" />
                  {t`Critical control point`}
                </span>
              )}
            </div>
          )}

          <p className="w-full truncate text-base font-medium leading-snug text-(--color-text-primary)">
            {step.instruction || t`Untitled step`}
          </p>

          {(tempLabel || hasMedia) && (
            <div className="flex items-center gap-2 text-sm text-(--color-text-secondary)">
              {tempLabel && <span>{t`${tempLabel} holding`}</span>}
              {tempLabel && hasMedia && <span>•</span>}
              {hasPhoto && (
                <span className="inline-flex items-center gap-1">
                  <ImageIcon className="size-4" />
                  {t`Media`}
                </span>
              )}
              {hasVideo && !hasPhoto && (
                <span className="inline-flex items-center gap-1">
                  <Video className="size-4" />
                  {t`Media`}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {showDragHandle && (
        <div className="flex size-8 shrink-0 items-center justify-center rounded-[6px] border border-(--color-border) bg-(--color-surface-content)">
          <GripVertical className="size-4 text-(--color-text-secondary)" />
        </div>
      )}
    </Button>
  );

  if (!ccpFlag) return card;

  return (
    <div className="relative">
      {card}
      <div className="absolute top-3 right-3">{ccpFlag}</div>
    </div>
  );
}
